import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  increment,
  startAfter,
  DocumentSnapshot,
  Timestamp,
  deleteField,
  QueryConstraint
} from 'firebase/firestore';
import { db } from './firebase';
import { Tag, TagType, ParentTagCategory, ArticleTag, TagHierarchyNode, TagFormData, TagSearchParams, FeaturedTag, FeaturedTagsConfig, EntityType, TagAssociation } from '@/types/tags';
import { twitterTagSyncService } from './twitter-tag-sync-service';
import { loggers } from './logger';
import { selectableChainIds, isChainId } from './chains';
import { propagateEntityRename, deleteEntityEdges } from './relationship-service';
import { stripVectors } from './server/strip-vectors';

/**
 * Known blockchain scopes an operator may assign as a tag `chains[]` value.
 * Derived from the chain registry (`shared/config/chains.js`) — the single source
 * of truth — so there is no duplicate list to keep in sync. To add a new selectable
 * chain, add it to the registry with `selectable: true`.
 */
export const KNOWN_CHAINS: readonly string[] = selectableChainIds();

/**
 * Normalize a tag `chains` array before writing to Firestore.
 * - Returns `undefined` if input is undefined or empty (preserves "not set",
 *   which is meaningful for chain-agnostic descriptor tags).
 * - Lowercases, trims, filters to KNOWN_CHAINS, dedupes, and sorts.
 * - Warns (console.warn) if any input value was filtered out as unknown.
 */
export function normalizeChains(input: string[] | undefined): string[] | undefined {
  if (!input || input.length === 0) return undefined;

  const known = new Set<string>(KNOWN_CHAINS);
  const seen = new Set<string>();
  const accepted: string[] = [];
  const filtered: string[] = [];

  for (const raw of input) {
    if (typeof raw !== 'string') continue;
    const normalized = raw.trim().toLowerCase();
    if (!normalized) continue;
    if (!known.has(normalized)) {
      filtered.push(raw);
      continue;
    }
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    accepted.push(normalized);
  }

  if (filtered.length > 0) {
    console.warn(
      `[tag-service] normalizeChains dropped unknown chain(s): ${filtered.join(', ')}. ` +
      `Known chains: ${KNOWN_CHAINS.join(', ')}. ` +
      `Add the chain to shared/config/chains.js with selectable:true if it's newly supported.`
    );
  }

  if (accepted.length === 0) return undefined;

  return accepted.sort();
}

class TagService {
  private tagCache = new Map<string, { tag: Tag; timestamp: number }>();
  private tagBatchCache = new Map<string, { tags: Map<string, Tag>; timestamp: number }>();
  private featuredTagsCache: { tags: Tag[]; timestamp: number } | null = null;
  private readonly TAG_CACHE_DURATION = 15 * 60 * 1000; // 15 minutes cache for tags (they change infrequently)
  private readonly BATCH_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes for batch cache
  private readonly FEATURED_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes for featured tags
  private readonly log = loggers.tags;

  private get tagsCollection() {
    if (!db) throw new Error('Firebase not initialized');
    return collection(db, 'tags');
  }

  private get articleTagsCollection() {
    if (!db) throw new Error('Firebase not initialized');
    return collection(db, 'article-tags');
  }

  private get featuredTagsCollection() {
    if (!db) throw new Error('Firebase not initialized');
    return collection(db, 'featured-tags');
  }

  // Basic CRUD Operations
  async createTag(tagData: TagFormData): Promise<string> {
    if (!db) throw new Error('Firebase not initialized');

    // Validate required fields
    if (!tagData.name?.trim()) {
      throw new Error('Tag name is required');
    }

    if (!tagData.type) {
      throw new Error('Tag type is required');
    }

    // NOTE: the legacy parent/child `type` taxonomy is a vestige of the pre-`entityType`
    // model. Entity relationships are now expressed via `entityType` + `associatedTags` +
    // the asset graph — never `tag.type`/`tag.parent`. The old "child requires a parent"
    // guard only broke entity edits (scripted entities default to type:'child' with no
    // parent), so it is intentionally removed. `type`/`parent` remain as inert fields.

    // Generate slug if not provided
    const slug = tagData.slug || this.generateSlug(tagData.name);

    // Validate slug uniqueness
    const slugValid = await this.validateTagSlug(slug);
    if (!slugValid) {
      throw new Error(`Tag with slug "${slug}" already exists`);
    }

    try {
      // Generate full path for SEO
      const fullPath = await this.generateFullPath({ ...tagData, slug });

      // Normalize multi-chain scope (Descout rebrand). Preserves undefined
      // so descriptor tags without any chain stay chain-agnostic.
      const normalizedChains = normalizeChains(tagData.chains);

      const tag: Omit<Tag, 'id'> = {
        name: tagData.name,
        slug,
        description: tagData.description,
        label: tagData.label,
        parent: tagData.parent || null,
        type: tagData.type,
        color: tagData.color,
        isActive: tagData.isActive,
        fullPath,
        createdAt: new Date(),
        updatedAt: new Date(),
        articleCount: 0,
        breakingNewsCount: 0, // Initialize breaking news count
        viewCount: 0,
        trending: false,
        weight: 50,

        // Chain scope (only written if defined — keeps descriptor tags chain-agnostic)
        ...(normalizedChains && { chains: normalizedChains }),

        // Entity fields (NEW)
        ...(tagData.entityType && { entityType: tagData.entityType }),
        ...(tagData.entityPriority && { entityPriority: tagData.entityPriority }),
        ...(tagData.hasGlossaryPage !== undefined && { hasGlossaryPage: tagData.hasGlossaryPage }),
        ...(tagData.glossaryContent && { glossaryContent: tagData.glossaryContent }),
        ...(tagData.seoExcerpt && { seoExcerpt: tagData.seoExcerpt }),
        ...(tagData.links && tagData.links.length > 0 && { links: tagData.links }),
        ...(tagData.images && tagData.images.length > 0 && { images: tagData.images }),
        ...(tagData.linkedAsset && { linkedAsset: tagData.linkedAsset }),
        ...(tagData.associatedTags && tagData.associatedTags.length > 0 && { associatedTags: tagData.associatedTags }),
        ...(tagData.migrationStatus && { migrationStatus: tagData.migrationStatus }),
        // Twitter handle linking
        ...(tagData.twitterHandles && tagData.twitterHandles.length > 0 && { twitterHandles: tagData.twitterHandles }),
        ...(tagData.primaryTwitterHandle && { primaryTwitterHandle: tagData.primaryTwitterHandle })
      };

      const docRef = await addDoc(this.tagsCollection, tag);

      // Clear cache to ensure new tag appears immediately
      this.tagBatchCache.clear();

      // Sync Twitter handles if provided
      if (tagData.twitterHandles && tagData.twitterHandles.length > 0) {
        // Run asynchronously to not block UI
        twitterTagSyncService.syncTagHandles(docRef.id, tagData.twitterHandles)
          .catch(err => this.log.error('Error syncing twitter handles', err));
      }

      return docRef.id;
    } catch (error) {
      throw new Error(`Failed to create tag: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getTag(id: string): Promise<Tag | null> {
    if (!db) return null;

    const docRef = doc(this.tagsCollection, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...stripVectors(data),
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
    } as Tag;
  }

  // Alias for getTag - for consistency with naming conventions
  async getTagById(id: string): Promise<Tag | null> {
    return this.getTag(id);
  }

  // Performance optimization: Batch tag fetching with enhanced cache
  async getTagsBatch(tagIds: string[]): Promise<Map<string, Tag>> {
    if (!db || tagIds.length === 0) return new Map();

    const uniqueTagIds = [...new Set(tagIds)]; // Remove duplicates
    const cacheKey = uniqueTagIds.sort().join(',');
    const now = Date.now();

    // Check batch cache first
    const batchCached = this.tagBatchCache.get(cacheKey);
    if (batchCached && (now - batchCached.timestamp) < this.BATCH_CACHE_DURATION) {
      return new Map(batchCached.tags);
    }

    const tagMap = new Map<string, Tag>();
    const uncachedTagIds: string[] = [];

    // Check individual cache
    uniqueTagIds.forEach(tagId => {
      const cached = this.tagCache.get(tagId);
      if (cached && (now - cached.timestamp) < this.TAG_CACHE_DURATION) {
        tagMap.set(tagId, cached.tag);
      } else {
        uncachedTagIds.push(tagId);
      }
    });

    // Only fetch uncached tags
    if (uncachedTagIds.length > 0) {
      // Use parallel document reads for better performance
      const tagPromises = uncachedTagIds.map(async (tagId) => {
        try {
          const docRef = doc(this.tagsCollection, tagId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();
            const tag = {
              id: docSnap.id,
              ...stripVectors(data),
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
              updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
            } as Tag;

            if (tag.isActive) {
              // Cache the fetched tag
              this.tagCache.set(tagId, { tag, timestamp: now });
              return { id: tagId, tag };
            }
          }
          return null;
        } catch (error) {
          // Error fetching tag - return null
          return null;
        }
      });

      const results = await Promise.all(tagPromises);
      results.forEach(result => {
        if (result) {
          tagMap.set(result.id, result.tag);
        }
      });
    }

    // Cache the batch result
    this.tagBatchCache.set(cacheKey, { tags: new Map(tagMap), timestamp: now });

    return tagMap;
  }

  async getTagBySlug(slug: string, type?: TagType): Promise<Tag | null> {
    if (!db) return null;

    let q = query(
      this.tagsCollection,
      where('slug', '==', slug),
      where('isActive', '==', true)
    );

    if (type) {
      q = query(
        this.tagsCollection,
        where('slug', '==', slug),
        where('type', '==', type),
        where('isActive', '==', true)
      );
    }

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...stripVectors(data),
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
    } as Tag;
  }

  /**
   * Find a tag by its associated Twitter handle
   * Searches the twitterHandles array for a matching handle
   * @param handle Twitter handle (without @) to search for
   * @returns Tag if found, null otherwise
   */
  async getTagByTwitterHandle(handle: string): Promise<Tag | null> {
    if (!db) return null;

    const normalizedHandle = handle.toLowerCase().replace('@', '');

    // Query tags where twitterHandles array contains this handle
    const q = query(
      this.tagsCollection,
      where('twitterHandles', 'array-contains', normalizedHandle),
      where('isActive', '==', true)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...stripVectors(data),
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
    } as Tag;
  }

  /**
   * Get a tag by slug first, falling back to Twitter handle lookup
   * This is useful when you might have either a slug or handle reference
   * @param slugOrHandle A tag slug or Twitter handle (without @)
   * @returns Tag if found by either method, null otherwise
   */
  async getTagBySlugOrHandle(slugOrHandle: string): Promise<Tag | null> {
    // First try by slug (most common case)
    const bySlug = await this.getTagBySlug(slugOrHandle);
    if (bySlug) return bySlug;

    // Fall back to Twitter handle lookup
    return this.getTagByTwitterHandle(slugOrHandle);
  }

  /**
   * Batch fetch tags by multiple slugs (optimized for tag mentions)
   * Chunks requests to handle Firestore 'in' query limit of 30 items
   * @param slugs Array of tag slugs to fetch
   * @returns Map of slug to Tag for O(1) lookup
   */
  async getTagsBySlugs(slugs: string[]): Promise<Map<string, Tag>> {
    if (!db || slugs.length === 0) return new Map();

    const uniqueSlugs = [...new Set(slugs)]; // Remove duplicates
    const tagMap = new Map<string, Tag>();

    // Helper to chunk array (Firestore 'in' supports max 30 items)
    const chunkArray = <T,>(arr: T[], size: number): T[][] => {
      const chunks: T[][] = [];
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
      }
      return chunks;
    };

    const slugChunks = chunkArray(uniqueSlugs, 30);

    // Fetch all chunks in parallel
    const queryPromises = slugChunks.map(async (chunk) => {
      try {
        const q = query(
          this.tagsCollection,
          where('slug', 'in', chunk),
          where('isActive', '==', true)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...stripVectors(data),
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
          } as Tag;
        });
      } catch (error) {
        this.log.error('Error fetching tag chunk', error);
        return [];
      }
    });

    const results = await Promise.all(queryPromises);

    // Flatten and build map
    results.flat().forEach(tag => {
      tagMap.set(tag.slug, tag);
      // Also cache individually for future lookups
      this.tagCache.set(tag.id, { tag, timestamp: Date.now() });
    });

    return tagMap;
  }

  async updateTag(id: string, updates: Partial<Tag> | Record<string, unknown>): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    // Get current tag to check if slug changed
    const currentTag = await this.getTag(id);
    if (!currentTag) throw new Error('Tag not found');

    // Clean updates to remove undefined values that would cause Firestore errors
    const cleanUpdates: Record<string, unknown> = {};

    // Fields that should be explicitly deleted if set to null
    const deletableFields = ['linkedAsset', 'associatedTags', 'glossaryContent', 'seoExcerpt', 'images', 'links', 'twitterHandles', 'primaryTwitterHandle'];

    // Only include defined values in the update
    Object.keys(updates).forEach(key => {
      const value = (updates as Record<string, unknown>)[key];

      // If value is null and it's a deletable field, use deleteField()
      if (value === null && deletableFields.includes(key)) {
        cleanUpdates[key] = deleteField();
      }
      // Otherwise only include if not undefined
      else if (value !== undefined) {
        cleanUpdates[key] = value;
      }
    });

    // Normalize chain scope if it's being updated. Skip if the caller explicitly
    // set it to a deleteField sentinel (i.e., null in deletableFields path — not
    // applicable here since `chains` isn't listed, but keep this defensive).
    if (Object.prototype.hasOwnProperty.call(cleanUpdates, 'chains')) {
      const raw = cleanUpdates.chains;
      if (Array.isArray(raw)) {
        const normalized = normalizeChains(raw as string[]);
        if (normalized === undefined) {
          // Caller passed an array that normalized to empty (e.g., only unknown
          // chains filtered out, or empty array). Don't overwrite with garbage —
          // drop from the update so existing value is preserved.
          delete cleanUpdates.chains;
        } else {
          cleanUpdates.chains = normalized;
        }
      }
    }

    // If slug changed, regenerate fullPath
    if (cleanUpdates.slug && cleanUpdates.slug !== currentTag.slug) {
      const tagData = {
        ...currentTag,
        ...cleanUpdates,
        slug: cleanUpdates.slug as string
      };
      cleanUpdates.fullPath = await this.generateFullPath(tagData);
    }

    // Always include updatedAt
    cleanUpdates.updatedAt = new Date();

    const docRef = doc(this.tagsCollection, id);
    await updateDoc(docRef, cleanUpdates as Partial<Tag>);

    // If this tag has children and slug changed, update their fullPaths too
    if (cleanUpdates.slug && cleanUpdates.slug !== currentTag.slug && currentTag.type === TagType.PARENT) {
      const childTags = await this.getChildTags(id);
      const batch = writeBatch(db);

      for (const child of childTags) {
        const newChildPath = `${cleanUpdates.slug}/${child.slug}`;
        const childRef = doc(this.tagsCollection, child.id);
        batch.update(childRef, { fullPath: newChildPath });
      }

      if (childTags.length > 0) {
        await batch.commit();
      }
    }

    // Propagate rename to denormalized fields on entity_relationships edges (non-blocking).
    // Fires when slug, name, or entityType changed — over-calling is harmless (idempotent rewrite).
    if (
      cleanUpdates.slug !== undefined ||
      cleanUpdates.name !== undefined ||
      cleanUpdates.entityType !== undefined
    ) {
      const newSlug = (cleanUpdates.slug as string | undefined) ?? currentTag.slug;
      const newName = (cleanUpdates.name as string | undefined) ?? currentTag.name;
      const newEntityType = (cleanUpdates.entityType as string | undefined | null) ?? currentTag.entityType ?? null;
      try {
        await propagateEntityRename(id, { slug: newSlug, name: newName, type: newEntityType });
      } catch (e) {
        console.warn('[relationship-service] rename propagation failed (non-blocking):', e);
      }
    }

    // IMPORTANT: Clear cache for this tag so changes appear immediately
    this.tagCache.delete(id);
    // Also clear batch cache to ensure fresh data
    this.tagBatchCache.clear();
    // Sync Twitter handles if updated
    if (updates.twitterHandles !== undefined && Array.isArray(updates.twitterHandles)) {
      // Run asynchronously
      twitterTagSyncService.syncTagHandles(id, updates.twitterHandles)
        .catch(err => this.log.error('Error syncing twitter handles', err));
    }
  }

  async deleteTag(id: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    // Remove from all articles first
    await this.removeTagFromAllArticles(id);

    // Clean up associatedTags references from other tags
    // Find all tags that have this tag in their associatedTags array
    try {
      const tagsWithAssociation = await getDocs(
        query(this.tagsCollection, where('associatedTags', 'array-contains', id))
      );

      if (!tagsWithAssociation.empty) {
        const batch = writeBatch(db);
        let updateCount = 0;

        tagsWithAssociation.docs.forEach(tagDoc => {
          const tagData = tagDoc.data();
          const updatedAssociations = (tagData.associatedTags || []).filter((assocId: string) => assocId !== id);
          batch.update(tagDoc.ref, { associatedTags: updatedAssociations });
          updateCount++;
        });

        await batch.commit();
        this.log.info(`Cleaned up ${updateCount} associatedTags references for deleted tag ${id}`);
      }
    } catch (err) {
      this.log.error('Error cleaning up associatedTags references', err);
      // Continue with deletion even if cleanup fails
    }

    // Cascade-delete typed relationship edges this entity touches (non-blocking — mirrors the
    // associatedTags cleanup + the rename hook; prevents dangling-edge accumulation in the graph).
    try {
      const removed = await deleteEntityEdges(id);
      if (removed > 0) this.log.info(`Deleted ${removed} typed relationship edge(s) for deleted tag ${id}`);
    } catch (e) {
      this.log.error('Error deleting typed relationship edges (non-blocking)', e);
    }

    // Delete the tag
    const docRef = doc(this.tagsCollection, id);
    await deleteDoc(docRef);

    // Clear cache for this tag and batch cache
    this.tagCache.delete(id);
    this.tagBatchCache.clear();
  }

  // Hierarchy & Organization
  // Legacy method - kept for backward compatibility, redirects to new method
  async getTagsByCategory(type: TagType, limitCount = 50): Promise<Tag[]> {
    return this.getTagsByType(type, limitCount);
  }

  async getAllTags(params: TagSearchParams = {}): Promise<Tag[]> {
    if (!db) return [];

    let q = query(this.tagsCollection);

    // Build query with filters
    const constraints = [];

    if (params.type) {
      constraints.push(where('type', '==', params.type));
    }

    if (params.parentCategory) {
      constraints.push(where('parentCategory', '==', params.parentCategory));
    }

    if (params.isActive !== undefined) {
      constraints.push(where('isActive', '==', params.isActive));
    }

    if (params.trending !== undefined) {
      constraints.push(where('trending', '==', params.trending));
    }

    if (params.parentId !== undefined) {
      constraints.push(where('parent', '==', params.parentId));
    }

    // Add ordering and limit
    constraints.push(orderBy('name'));

    if (params.limit) {
      constraints.push(limit(params.limit));
    }

    q = query(this.tagsCollection, ...constraints);

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...stripVectors(data),
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as Tag;
    });
  }

  async getChildTags(parentId: string): Promise<Tag[]> {
    if (!db) return [];

    const q = query(
      this.tagsCollection,
      where('parent', '==', parentId),
      where('isActive', '==', true),
      orderBy('name')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...stripVectors(data),
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as Tag;
    });
  }

  /**
   * Get tags that have a specific linked asset
   * Used by Coin/Collection pages to find their associated tags
   * Handles case-insensitive matching for token symbols (BTC, btc, etc.)
   */
  async getTagsByLinkedAsset(assetType: 'token' | 'nft', assetId: string): Promise<Tag[]> {
    if (!db) return [];

    try {
      // For tokens, try both the original case and uppercase (most tokens use uppercase symbols)
      const assetIdsToTry = assetType === 'token'
        ? [assetId, assetId.toUpperCase()]
        : [assetId];

      // Remove duplicates (e.g., if assetId is already uppercase)
      const uniqueAssetIds = [...new Set(assetIdsToTry)];

      // Query for all possible case variants
      const queryPromises = uniqueAssetIds.map(async (id) => {
        const q = query(
          this.tagsCollection,
          where('linkedAsset.type', '==', assetType),
          where('linkedAsset.assetId', '==', id),
          where('isActive', '==', true)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...stripVectors(data),
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
          } as Tag;
        });
      });

      const results = await Promise.all(queryPromises);

      // Flatten and deduplicate by tag ID
      const tagMap = new Map<string, Tag>();
      results.flat().forEach(tag => {
        tagMap.set(tag.id, tag);
      });

      return Array.from(tagMap.values());
    } catch (error) {
      this.log.error('Error getting tags by linked asset', error);
      return [];
    }
  }


  async getTagHierarchy(): Promise<TagHierarchyNode[]> {
    if (!db) return [];

    // Get all active tags
    const allTags = await this.getAllTags({ isActive: true });

    // Build hierarchy
    return this.buildTagHierarchy(allTags);
  }

  // Article Integration
  async addTagsToArticle(
    articleId: string,
    tagIds: string[],
    weights: number[] = [],
    addedBy: string = 'system'
  ): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const batch = writeBatch(db);

    // Remove existing tags for this article first
    await this.removeAllTagsFromArticle(articleId);

    tagIds.forEach((tagId, index) => {
      const articleTagRef = doc(this.articleTagsCollection);
      const articleTag: ArticleTag = {
        articleId,
        tagId,
        weight: weights[index] || 50,
        addedAt: new Date(),
        addedBy
      };

      // Relationship only. articleCount (published-only) is maintained by
      // content-service on publish/unpublish/delete transitions — NOT on tag attach.
      batch.set(articleTagRef, articleTag);
    });

    await batch.commit();
  }

  /**
   * Adjust the PUBLISHED-article count for the given tags by `delta`.
   * articleCount tracks published, non-deleted articles only (see the
   * /api/admin/tag-count-migration reconciler), so content-service calls this on
   * publish / unpublish / delete / restore — never on a plain tag attach.
   */
  async adjustArticleCounts(tagIds: string[], delta: number): Promise<void> {
    if (!db || !tagIds || tagIds.length === 0 || delta === 0) return;
    const batch = writeBatch(db);
    for (const tagId of tagIds) {
      batch.update(doc(this.tagsCollection, tagId), { articleCount: increment(delta) });
    }
    await batch.commit();
  }

  async removeTagsFromArticle(articleId: string, tagIds: string[]): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const batch = writeBatch(db);

    for (const tagId of tagIds) {
      // Find and delete article-tag relationships
      const q = query(
        this.articleTagsCollection,
        where('articleId', '==', articleId),
        where('tagId', '==', tagId)
      );

      const snapshot = await getDocs(q);
      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Decrement tag article count
      const tagRef = doc(this.tagsCollection, tagId);
      batch.update(tagRef, { articleCount: increment(-1) });
    }

    await batch.commit();
  }

  async removeAllTagsFromArticle(articleId: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const q = query(
      this.articleTagsCollection,
      where('articleId', '==', articleId)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    // Relationship cleanup only. articleCount (published-only) is maintained by
    // content-service on publish/unpublish/delete transitions — NOT here.
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  }

  /**
   * Add a single tag to an article (migration helper)
   */
  async addTagToArticle(articleId: string, tagId: string, addedBy: string = 'system', weight: number = 50): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const batch = writeBatch(db);

    const articleTagRef = doc(this.articleTagsCollection);
    const articleTag: ArticleTag = {
      articleId,
      tagId,
      weight,
      addedAt: new Date(),
      addedBy
    };

    batch.set(articleTagRef, articleTag);

    // Update tag article count
    const tagRef = doc(this.tagsCollection, tagId);
    batch.update(tagRef, { articleCount: increment(1) });

    await batch.commit();
  }

  /**
   * Remove a single tag from an article (migration helper)
   */
  async removeTagFromArticle(articleId: string, tagId: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const q = query(
      this.articleTagsCollection,
      where('articleId', '==', articleId),
      where('tagId', '==', tagId)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Decrement tag article count
    const tagRef = doc(this.tagsCollection, tagId);
    batch.update(tagRef, { articleCount: increment(-1) });

    await batch.commit();
  }

  async getArticleTags(articleId: string): Promise<Tag[]> {
    if (!db) return [];

    const q = query(
      this.articleTagsCollection,
      where('articleId', '==', articleId),
      orderBy('weight', 'desc')
    );

    const snapshot = await getDocs(q);
    const tagIds = snapshot.docs.map(doc => doc.data().tagId);

    // Batch fetch all tags in parallel instead of sequential N+1 queries
    const tagMap = await this.getTagsBatch(tagIds);
    return tagIds.map(id => tagMap.get(id)).filter((t): t is Tag => !!t && t.isActive);
  }

  async getArticlesByTag(tagId: string, limitCount = 10): Promise<string[]> {
    if (!db) return [];

    const q = query(
      this.articleTagsCollection,
      where('tagId', '==', tagId),
      orderBy('weight', 'desc'),
      limit(limitCount * 2) // Fetch more to account for potential duplicates
    );

    const snapshot = await getDocs(q);
    const articleIds = snapshot.docs.map(doc => doc.data().articleId);
    
    // Deduplicate in case of duplicate article-tag entries (from race conditions)
    const uniqueIds = [...new Set(articleIds)];
    return uniqueIds.slice(0, limitCount);
  }

  // Discovery & Analytics
  async searchTags(searchQuery: string, type?: TagType): Promise<Tag[]> {
    if (!db) return [];

    // Simple search - can be enhanced with Algolia/full-text search later
    const params: TagSearchParams = { isActive: true };
    if (type) params.type = type;

    const allTags = await this.getAllTags(params);

    return allTags.filter(tag =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tag.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  /**
   * Return all tags whose `chains` array includes the given chain id.
   *
   * Membership semantics (multi-chain hub):
   *   - `array-contains` → cross-chain entities appear in every hub they belong to.
   *   - `chains[0]` is the entity's HOME chain (use `getHomeChain()` from chains.ts).
   *   - Passing `trending: true` restricts to trending entities for the per-chain
   *     trending rail (backed by the `chains × trending × name` composite index).
   *
   * @param chain  A known chain id (validated against `KNOWN_CHAINS`/`selectableChainIds`).
   * @param opts   Optional filters:
   *   - `type`      — restrict to a specific `EntityType`
   *   - `trending`  — restrict to trending entities
   *   - `isActive`  — defaults to `true`; pass `false` only for admin tooling
   *   - `limit`     — max results (default 100)
   *
   * @throws Error if `chain` is not a known chain id.
   */
  async getTagsByChain(
    chain: string,
    opts: { type?: EntityType; trending?: boolean; isActive?: boolean; limit?: number } = {}
  ): Promise<Tag[]> {
    if (!db) return [];

    // Validate chain against the registry (single source of truth).
    if (!isChainId(chain)) {
      throw new Error(
        `[tag-service] getTagsByChain: unknown chain id "${chain}". ` +
        `Known chains: ${KNOWN_CHAINS.join(', ')}. ` +
        `Add it to shared/config/chains.js with selectable:true if newly supported.`
      );
    }

    // Normalize: registry ids + stored tag.chains[] are lowercase, and Firestore
    // array-contains is case-sensitive (isChainId accepts mixed case, so 'Base' would
    // otherwise match zero docs).
    chain = chain.toLowerCase();

    const { type, trending, isActive = true, limit: limitCount = 100 } = opts;

    try {
      const constraints: QueryConstraint[] = [
        where('chains', 'array-contains', chain),
        where('isActive', '==', isActive),
      ];

      if (type !== undefined) {
        constraints.push(where('entityType', '==', type));
      }

      if (trending !== undefined) {
        constraints.push(where('trending', '==', trending));
      }

      constraints.push(orderBy('name'));
      constraints.push(limit(limitCount));

      const q = query(this.tagsCollection, ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...stripVectors(data),
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        } as Tag;
      });
    } catch (error) {
      // Fallback when no composite index covers this filter permutation: query ONLY the
      // single `array-contains` (served by the automatic array index — never needs a
      // composite), then apply isActive/type/trending + sort + limit client-side. This
      // cannot itself require an un-authored index, so it degrades cleanly for EVERY
      // permutation (not just the orderBy-dropped case).
      this.log.error(`getTagsByChain(${chain}) compound query failed; using single-filter fallback`, error);
      try {
        const snapshot = await getDocs(
          query(this.tagsCollection, where('chains', 'array-contains', chain), limit(500))
        );

        let tags = snapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...stripVectors(data),
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
          } as Tag;
        });

        tags = tags.filter(t => t.isActive === isActive);
        if (type !== undefined) tags = tags.filter(t => t.entityType === type);
        if (trending !== undefined) tags = tags.filter(t => t.trending === trending);

        tags.sort((a, b) => a.name.localeCompare(b.name));
        return tags.slice(0, limitCount);
      } catch (fallbackError) {
        this.log.error(`getTagsByChain(${chain}) fallback also failed`, fallbackError);
        return [];
      }
    }
  }

  async getTrendingTags(type?: TagType, limitCount = 10): Promise<Tag[]> {
    if (!db) return [];

    const params: TagSearchParams = {
      isActive: true,
      trending: true,
      limit: limitCount
    };

    if (type) params.type = type;

    return this.getAllTags(params);
  }

  async getPopularTags(type?: TagType, limitCount = 10): Promise<Tag[]> {
    if (!db) return [];

    try {
      let q = query(this.tagsCollection);

      if (type) {
        q = query(
          this.tagsCollection,
          where('type', '==', type),
          where('isActive', '==', true),
          orderBy('articleCount', 'desc'),
          limit(limitCount)
        );
      } else {
        q = query(
          this.tagsCollection,
          where('isActive', '==', true),
          orderBy('articleCount', 'desc'),
          limit(limitCount)
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...stripVectors(data),
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
        } as Tag;
      });
    } catch (error) {
      // Fallback: Return simpler query without ordering if compound index fails
      try {
        const q = query(
          this.tagsCollection,
          where('isActive', '==', true),
          limit(limitCount)
        );
        const snapshot = await getDocs(q);
        const tags = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...stripVectors(data),
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
          } as Tag;
        });
        // Sort client-side if needed
        return tags.sort((a, b) => (b.articleCount || 0) - (a.articleCount || 0));
      } catch (fallbackError) {
        return [];
      }
    }
  }

  async updateTagAnalytics(tagId: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const tagRef = doc(this.tagsCollection, tagId);
    await updateDoc(tagRef, {
      viewCount: increment(1),
      updatedAt: new Date()
    });
  }

  // Bulk Operations
  async bulkCreateTags(tags: TagFormData[]): Promise<string[]> {
    if (!db) throw new Error('Firebase not initialized');

    const batch = writeBatch(db);
    const tagIds: string[] = [];

    for (const tagData of tags) {
      const tagRef = doc(this.tagsCollection);
      const fullPath = await this.generateFullPath(tagData);
      const normalizedChains = normalizeChains(tagData.chains);

      const tag: Omit<Tag, 'id'> = {
        ...tagData,
        slug: this.generateSlug(tagData.name),
        parent: tagData.parent || null,
        fullPath,
        createdAt: new Date(),
        updatedAt: new Date(),
        articleCount: 0,
        breakingNewsCount: 0, // Initialize breaking news count
        viewCount: 0,
        trending: false,
        weight: 50,
        // Convert null to undefined for linkedAsset (null is used in FormData to indicate deletion)
        linkedAsset: tagData.linkedAsset ?? undefined,
        // Normalized chain scope (undefined stays chain-agnostic)
        chains: normalizedChains
      };

      batch.set(tagRef, tag);
      tagIds.push(tagRef.id);
    }

    await batch.commit();
    return tagIds;
  }

  async bulkUpdateTagCounts(): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    // PERFORMANCE OPTIMIZATION: Fetch all article-tags once, count in memory
    // This replaces N queries (one per tag) with 1 query + in-memory counting

    // Get all tags
    const tagsSnapshot = await getDocs(this.tagsCollection);

    // Get ALL article-tags in a single query
    const allArticleTagsSnapshot = await getDocs(this.articleTagsCollection);

    // Count articles per tag in memory
    const countsByTag = new Map<string, number>();
    allArticleTagsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const tagId = data.tagId;
      countsByTag.set(tagId, (countsByTag.get(tagId) || 0) + 1);
    });

    // Update all tags in a batch
    const batch = writeBatch(db);
    tagsSnapshot.docs.forEach(tagDoc => {
      const tagId = tagDoc.id;
      const articleCount = countsByTag.get(tagId) || 0;
      batch.update(tagDoc.ref, { articleCount });
    });

    await batch.commit();

    // Updated article counts for tags
  }

  /**
   * Update breaking news count for multiple tags (called when breaking news is created/deleted)
   * @param tagIds Array of tag IDs to update
   * @param delta +1 for create, -1 for delete
   */
  async updateBreakingNewsCount(tagIds: string[], delta: number): Promise<void> {
    if (!db || tagIds.length === 0) return;

    try {
      const batch = writeBatch(db);

      tagIds.forEach(tagId => {
        const tagRef = doc(this.tagsCollection, tagId);
        batch.update(tagRef, {
          breakingNewsCount: increment(delta),
          updatedAt: new Date()
        });
      });

      await batch.commit();

      // Clear cache for updated tags
      tagIds.forEach(tagId => this.tagCache.delete(tagId));
      this.tagBatchCache.clear();
    } catch (error) {
      this.log.error('Error updating breaking news counts', error);
      // Non-blocking: Don't throw to avoid breaking breaking news operations
    }
  }

  /**
   * Bulk update all breaking news counts from actual data
   * Used for migration and recovery
   */
  async bulkUpdateBreakingNewsCounts(): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    // PERFORMANCE OPTIMIZATION: Fetch all breaking news once, count in memory
    // Similar pattern to bulkUpdateTagCounts

    try {
      // Get all tags
      const tagsSnapshot = await getDocs(this.tagsCollection);

      // Get ALL published breaking news directly from Firestore
      const breakingNewsQuery = query(
        collection(db, 'breaking_news'),
        where('status', '==', 'published'),
        where('isDeleted', '==', false)
      );

      const breakingNewsSnapshot = await getDocs(breakingNewsQuery);

      // Count breaking news per tag in memory
      const countsByTag = new Map<string, number>();
      breakingNewsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const tags = data.tags || [];
        tags.forEach((tagId: string) => {
          countsByTag.set(tagId, (countsByTag.get(tagId) || 0) + 1);
        });
      });

      // Update all tags in batches (Firestore limit: 500 operations per batch)
      const tagDocs = tagsSnapshot.docs;
      const batchSize = 500;

      for (let i = 0; i < tagDocs.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchDocs = tagDocs.slice(i, i + batchSize);

        batchDocs.forEach(tagDoc => {
          const tagId = tagDoc.id;
          const breakingNewsCount = countsByTag.get(tagId) || 0;
          batch.update(tagDoc.ref, { breakingNewsCount });
        });

        await batch.commit();
      }

      // Clear caches
      this.tagCache.clear();
      this.tagBatchCache.clear();

      this.log.info('Updated breaking news counts', {
        tagsUpdated: tagDocs.length,
        breakingNewsItems: breakingNewsSnapshot.docs.length,
        tagsWithNews: countsByTag.size
      });
    } catch (error) {
      this.log.error('Error bulk updating breaking news counts', error);
      throw error;
    }
  }

  // Tag validation and utilities
  async validateTagSlug(slug: string, excludeId?: string): Promise<boolean> {
    if (!db) return true;

    const q = query(
      this.tagsCollection,
      where('slug', '==', slug)
    );

    const snapshot = await getDocs(q);

    if (excludeId) {
      // Check if any existing tags with this slug have different IDs
      return !snapshot.docs.some(doc => doc.id !== excludeId);
    }

    return snapshot.empty;
  }

  async getTagSuggestions(prefix: string, limitCount = 5): Promise<Tag[]> {
    if (!db || prefix.length < 2) return [];

    const q = query(
      this.tagsCollection,
      where('isActive', '==', true),
      orderBy('name'),
      limit(limitCount * 3) // Get more to filter client-side
    );

    const snapshot = await getDocs(q);
    const allTags = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...stripVectors(data),
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as Tag;
    });

    return allTags
      .filter(tag => tag.name.toLowerCase().startsWith(prefix.toLowerCase()))
      .slice(0, limitCount);
  }

  // Private helper methods
  private generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  private async removeTagFromAllArticles(tagId: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const q = query(
      this.articleTagsCollection,
      where('tagId', '==', tagId)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  }

  private buildTagHierarchy(tags: Tag[]): TagHierarchyNode[] {
    const tagMap = new Map<string, Tag>();
    const rootTags: TagHierarchyNode[] = [];

    // Create tag map
    tags.forEach(tag => tagMap.set(tag.id, tag));

    // Build hierarchy
    tags.forEach(tag => {
      if (!tag.parent) {
        // Root tag
        rootTags.push({
          tag,
          children: this.getChildrenRecursive(tag.id, tagMap, 0),
          depth: 0
        });
      }
    });

    return rootTags.sort((a, b) => a.tag.name.localeCompare(b.tag.name));
  }

  private getChildrenRecursive(
    parentId: string,
    tagMap: Map<string, Tag>,
    depth: number
  ): TagHierarchyNode[] {
    const children: TagHierarchyNode[] = [];

    tagMap.forEach(tag => {
      if (tag.parent === parentId) {
        children.push({
          tag,
          children: this.getChildrenRecursive(tag.id, tagMap, depth + 1),
          depth: depth + 1
        });
      }
    });

    return children.sort((a, b) => a.tag.name.localeCompare(b.tag.name));
  }

  // Helper method to generate full path for SEO URLs
  private async generateFullPath(tagData: TagFormData | (Tag & { slug: string })): Promise<string> {
    // Post-migration: fullPath is now identical to slug (flat structure)
    // We keep this method for backward compatibility and potential future use
    const slug = tagData.slug || this.generateSlug(tagData.name);
    return slug;
  }

  // New methods for hierarchical system
  async getTagsByType(type: TagType, limitCount = 50): Promise<Tag[]> {
    if (!db) return [];

    const q = query(
      this.tagsCollection,
      where('type', '==', type),
      where('isActive', '==', true),
      orderBy('name'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...stripVectors(data),
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
      } as Tag;
    });
  }

  async getTagsByParentCategory(parentCategory: ParentTagCategory, limitCount = 50): Promise<Tag[]> {
    if (!db) return [];

    try {
      const q = query(
        this.tagsCollection,
        where('parentCategory', '==', parentCategory),
        where('isActive', '==', true),
        orderBy('name'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...stripVectors(data),
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
        } as Tag;
      });
    } catch (error) {
      // Fallback: Return simpler query without ordering if compound index fails
      try {
        const q = query(
          this.tagsCollection,
          where('parentCategory', '==', parentCategory),
          where('isActive', '==', true),
          limit(limitCount)
        );
        const snapshot = await getDocs(q);
        const tags = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...stripVectors(data),
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
          } as Tag;
        });
        // Sort client-side by name
        return tags.sort((a, b) => a.name.localeCompare(b.name));
      } catch (fallbackError) {
        return [];
      }
    }
  }

  async getTagByPath(path: string): Promise<Tag | null> {
    if (!db) return null;

    const q = query(
      this.tagsCollection,
      where('fullPath', '==', path),
      where('isActive', '==', true),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...stripVectors(data),
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
    } as Tag;
  }

  // Featured Tags Management
  async getFeaturedTagsConfig(): Promise<FeaturedTagsConfig | null> {
    if (!db) return null;

    try {
      const q = query(this.featuredTagsCollection, limit(1));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      const config = {
        id: doc.id,
        ...data,
        lastUpdated: data.lastUpdated instanceof Timestamp ? data.lastUpdated.toDate() : data.lastUpdated,
        featuredTags: data.featuredTags?.map((ft: Record<string, unknown>) => ({
          ...ft,
          createdAt: ft.createdAt instanceof Timestamp ? ft.createdAt.toDate() : ft.createdAt,
          updatedAt: ft.updatedAt instanceof Timestamp ? ft.updatedAt.toDate() : ft.updatedAt
        })) || []
      } as FeaturedTagsConfig;

      return config;
    } catch (error) {
      return null;
    }
  }

  async updateFeaturedTagsConfig(
    featuredTags: Omit<FeaturedTag, 'id' | 'createdAt' | 'updatedAt'>[],
    updatedBy: string
  ): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    // Get existing config or create new one
    const existingConfig = await this.getFeaturedTagsConfig();

    const now = new Date();
    const featuredTagsWithDates: FeaturedTag[] = featuredTags.map((ft, index) => ({
      id: `featured-${index}`,
      ...ft,
      createdAt: now,
      updatedAt: now
    }));

    const config: Omit<FeaturedTagsConfig, 'id'> = {
      featuredTags: featuredTagsWithDates,
      lastUpdated: now,
      updatedBy
    };

    if (existingConfig) {
      // Update existing
      const docRef = doc(this.featuredTagsCollection, existingConfig.id);
      await updateDoc(docRef, config);
    } else {
      // Create new
      await addDoc(this.featuredTagsCollection, config);
    }
  }

  async getFeaturedParentTags(): Promise<Tag[]> {
    if (!db) return [];

    const now = Date.now();

    // Check cache first
    if (this.featuredTagsCache && (now - this.featuredTagsCache.timestamp) < this.FEATURED_CACHE_DURATION) {
      return this.featuredTagsCache.tags;
    }

    try {
      // Get parent tags and config in parallel
      const [allParentTags, config] = await Promise.all([
        this.getTagsByType(TagType.PARENT, 50),
        this.getFeaturedTagsConfig()
      ]);

      if (allParentTags.length === 0) {
        this.featuredTagsCache = { tags: [], timestamp: now };
        return [];
      }

      if (!config || !config.featuredTags.length) {
        // Return a reasonable number of parent tags as fallback
        const fallbackTags = allParentTags
          .filter(tag => tag.isActive && (tag.articleCount || 0) > 0)
          .sort((a, b) => (b.articleCount || 0) - (a.articleCount || 0))
          .slice(0, 6);

        this.featuredTagsCache = { tags: fallbackTags, timestamp: now };
        return fallbackTags;
      }

      // Filter only enabled featured tags and sort by order
      const enabledFeatured = config.featuredTags
        .filter(ft => ft.isEnabled)
        .sort((a, b) => a.order - b.order);

      if (enabledFeatured.length === 0) {
        const fallbackTags = allParentTags.slice(0, 6);
        this.featuredTagsCache = { tags: fallbackTags, timestamp: now };
        return fallbackTags;
      }

      // Batch fetch tag data for better performance
      const featuredTagIds = enabledFeatured.map(ft => ft.tagId);
      const tagMap = await this.getTagsBatch(featuredTagIds);

      const tags: Tag[] = [];
      for (const featured of enabledFeatured) {
        const tag = tagMap.get(featured.tagId);
        if (tag && tag.isActive && tag.type === TagType.PARENT) {
          tags.push(tag);
        } else {
          // Try to find the tag by name if ID lookup failed
          if (!tag && featured.title) {
            const fallbackTag = allParentTags.find(t =>
              t.name.toLowerCase() === featured.title.toLowerCase() ||
              t.label?.toLowerCase() === featured.title.toLowerCase()
            );
            if (fallbackTag) {
              tags.push(fallbackTag);
            }
          }
        }
      }

      // If we couldn't load any featured tags (due to ID mismatches), fall back to default
      const resultTags = tags.length > 0 ? tags : allParentTags.slice(0, 6);

      // Cache the result
      this.featuredTagsCache = { tags: resultTags, timestamp: now };
      return resultTags;

    } catch (error) {
      // Error loading featured parent tags - use fallback
      try {
        const fallbackTags = await this.getTagsByType(TagType.PARENT, 6);
        this.featuredTagsCache = { tags: fallbackTags, timestamp: now };
        return fallbackTags;
      } catch (fallbackError) {
        this.featuredTagsCache = { tags: [], timestamp: now };
        return [];
      }
    }
  }

  // Cache management methods
  clearCache(): void {
    this.tagCache.clear();
    this.tagBatchCache.clear();
    this.featuredTagsCache = null;
  }

  clearTagCache(tagId?: string): void {
    if (tagId) {
      this.tagCache.delete(tagId);
      // Clear any batch caches that might contain this tag
      this.tagBatchCache.clear();
    } else {
      this.tagCache.clear();
      this.tagBatchCache.clear();
    }
  }

  clearFeaturedCache(): void {
    this.featuredTagsCache = null;
  }

  // Get cache statistics for debugging
  getCacheStats() {
    const now = Date.now();
    return {
      tagCache: {
        size: this.tagCache.size,
        validEntries: Array.from(this.tagCache.values()).filter(
          entry => (now - entry.timestamp) < this.TAG_CACHE_DURATION
        ).length
      },
      batchCache: {
        size: this.tagBatchCache.size,
        validEntries: Array.from(this.tagBatchCache.values()).filter(
          entry => (now - entry.timestamp) < this.BATCH_CACHE_DURATION
        ).length
      },
      featuredCache: {
        exists: !!this.featuredTagsCache,
        isValid: !!(this.featuredTagsCache && (now - this.featuredTagsCache.timestamp) < this.FEATURED_CACHE_DURATION)
      }
    };
  }

  // ============================================
  // NEW: Entity System - Tag Associations
  // ============================================

  /**
   * Get all tags associated with a specific tag
   */
  async getAssociatedTags(tagId: string): Promise<Tag[]> {
    if (!db) return [];

    const tag = await this.getTag(tagId);
    if (!tag || !tag.associatedTags || tag.associatedTags.length === 0) {
      return [];
    }

    // Fetch all associated tags. Per-id isolation is load-bearing: Firestore rules
    // DENY public reads of inactive/excluded tag docs (they throw permission-denied
    // rather than returning isActive:false), and one deactivated association must
    // not kill the whole list — an unhandled throw here silently downgraded entity
    // dossiers off the expanded article path onto the stale precomputed fallback
    // (the 2026-07-09 Cambria gap: 5 of its 8 associations were deactivated).
    const associatedTags: Tag[] = [];
    for (const associatedTagId of tag.associatedTags) {
      try {
        const associatedTag = await this.getTag(associatedTagId);
        if (associatedTag && associatedTag.isActive) {
          associatedTags.push(associatedTag);
        }
      } catch {
        // unreadable (deactivated/excluded) association — skip it
      }
    }

    return associatedTags;
  }

  /**
   * Add a bidirectional association between two tags
   * This mirrors the asset tagging pattern but for tag-to-tag relationships
   */
  async addTagAssociation(tagId: string, associatedTagId: string, addedBy?: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    if (tagId === associatedTagId) {
      throw new Error('Cannot associate a tag with itself');
    }

    // Get both tags
    const [tag, associatedTag] = await Promise.all([
      this.getTag(tagId),
      this.getTag(associatedTagId)
    ]);

    if (!tag) throw new Error('Source tag not found');
    if (!associatedTag) throw new Error('Associated tag not found');

    // Update both tags to include the association (bidirectional)
    const tag1Associations = new Set(tag.associatedTags || []);
    const tag2Associations = new Set(associatedTag.associatedTags || []);

    tag1Associations.add(associatedTagId);
    tag2Associations.add(tagId);

    // Update both tags
    await Promise.all([
      this.updateTag(tagId, {
        associatedTags: Array.from(tag1Associations)
      }),
      this.updateTag(associatedTagId, {
        associatedTags: Array.from(tag2Associations)
      })
    ]);

    // Clear cache for both tags
    this.clearTagCache(tagId);
    this.clearTagCache(associatedTagId);
  }

  /**
   * Remove a bidirectional association between two tags
   */
  async removeTagAssociation(tagId: string, associatedTagId: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    // Get both tags
    const [tag, associatedTag] = await Promise.all([
      this.getTag(tagId),
      this.getTag(associatedTagId)
    ]);

    if (!tag || !associatedTag) return; // Nothing to remove

    // Remove from both tags
    const tag1Associations = new Set(tag.associatedTags || []);
    const tag2Associations = new Set(associatedTag.associatedTags || []);

    tag1Associations.delete(associatedTagId);
    tag2Associations.delete(tagId);

    // Update both tags
    await Promise.all([
      this.updateTag(tagId, {
        associatedTags: Array.from(tag1Associations)
      }),
      this.updateTag(associatedTagId, {
        associatedTags: Array.from(tag2Associations)
      })
    ]);

    // Clear cache for both tags
    this.clearTagCache(tagId);
    this.clearTagCache(associatedTagId);
  }

  /**
   * Set all associations for a tag (replaces existing)
   */
  async setTagAssociations(tagId: string, associatedTagIds: string[]): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    // Get current associations
    const tag = await this.getTag(tagId);
    if (!tag) throw new Error('Tag not found');

    const currentAssociations = new Set(tag.associatedTags || []);
    const newAssociations = new Set(associatedTagIds);

    // Determine what to add and what to remove
    const toAdd = Array.from(newAssociations).filter(id => !currentAssociations.has(id));
    const toRemove = Array.from(currentAssociations).filter(id => !newAssociations.has(id));

    // Add new associations
    for (const associatedTagId of toAdd) {
      await this.addTagAssociation(tagId, associatedTagId);
    }

    // Remove old associations
    for (const associatedTagId of toRemove) {
      await this.removeTagAssociation(tagId, associatedTagId);
    }
  }

  /**
   * Get all articles for a tag AND its associated tags (expanded content)
   * This is key to showing content across the ecosystem
   */
  async getExpandedArticles(tagId: string, limitCount = 20): Promise<Array<{ articleId: string; source: 'direct' | 'association'; sourceTagId?: string; sourceTagName?: string }>> {
    if (!db) return [];

    type ExpandedArticle = { articleId: string; source: 'direct' | 'association'; sourceTagId?: string; sourceTagName?: string };

    // Get direct articles
    const directArticleIds = await this.getArticlesByTag(tagId, limitCount);
    const results: ExpandedArticle[] = directArticleIds.map(articleId => ({
      articleId,
      source: 'direct' as const
    }));

    // Get associated tags
    const associatedTags = await this.getAssociatedTags(tagId);

    // Get articles from associated tags (limited to avoid overwhelming results)
    const remainingLimit = Math.max(0, limitCount - results.length);
    if (remainingLimit > 0 && associatedTags.length > 0) {
      const articlesPerTag = Math.ceil(remainingLimit / associatedTags.length);

      for (const associatedTag of associatedTags) {
        const associatedArticleIds = await this.getArticlesByTag(associatedTag.id, articlesPerTag);

        // Only add articles not already in results
        const existingArticleIds = new Set(results.map(r => r.articleId));
        for (const articleId of associatedArticleIds) {
          if (!existingArticleIds.has(articleId)) {
            results.push({
              articleId,
              source: 'association' as const,
              sourceTagId: associatedTag.id,
              sourceTagName: associatedTag.name
            });
          }
        }
      }
    }

    return results.slice(0, limitCount);
  }

  /**
   * Get tags by entity type
   */
  async getTagsByEntityType(entityType: EntityType, limitCount = 50): Promise<Tag[]> {
    if (!db) return [];

    try {
      const q = query(
        this.tagsCollection,
        where('entityType', '==', entityType),
        where('isActive', '==', true),
        orderBy('name'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...stripVectors(data),
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
        } as Tag;
      });
    } catch (error) {
      // Fallback: Get all active tags and filter client-side
      const allTags = await this.getAllTags({ isActive: true, limit: 200 });
      return allTags
        .filter(tag => tag.entityType === entityType)
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, limitCount);
    }
  }

  /**
   * Update tag glossary content (for entity pages)
   */
  async updateTagGlossary(
    tagId: string,
    glossaryData: {
      glossaryContent?: string;
      links?: Array<{ url: string; title?: string; type: string }>;
      images?: Array<{ url: string; caption?: string; isPrimary?: boolean; order?: number }>;
      seoExcerpt?: string;
    }
  ): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    const updates: Record<string, unknown> = {
      updatedAt: new Date()
    };

    if (glossaryData.glossaryContent !== undefined) {
      updates.glossaryContent = glossaryData.glossaryContent;
    }

    if (glossaryData.links !== undefined) {
      updates.links = glossaryData.links;
    }

    if (glossaryData.images !== undefined) {
      updates.images = glossaryData.images;
    }

    if (glossaryData.seoExcerpt !== undefined) {
      updates.seoExcerpt = glossaryData.seoExcerpt;
    }

    await this.updateTag(tagId, updates);
  }

  /**
   * Get all entity tags (tags with entityType set)
   */
  async getAllEntityTags(limitCount = 100): Promise<Tag[]> {
    if (!db) return [];

    try {
      // Try to get tags where entityType is not null
      // Note: Firestore doesn't support != null, so we'll need to get all and filter
      const allTags = await this.getAllTags({ isActive: true, limit: 200 });

      return allTags
        .filter(tag => tag.entityType !== undefined && tag.entityType !== null)
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, limitCount);
    } catch (error) {
      this.log.error('Error getting entity tags', error);
      return [];
    }
  }

  /**
   * Search tags with entity type filtering
   */
  async searchTagsByEntityType(searchQuery: string, entityType?: EntityType): Promise<Tag[]> {
    if (!db) return [];

    // Get all tags matching the search
    const allMatches = await this.searchTags(searchQuery);

    // Filter by entity type if specified
    if (entityType) {
      return allMatches.filter(tag => tag.entityType === entityType);
    }

    return allMatches;
  }

  /**
   * Assign a tag to a user (adds tag to user's tags array)
   * Used for auto-assigning approved entity request tags to requesters
   */
  async assignTagToUser(userId: string, tagId: string): Promise<void> {
    if (!db) throw new Error('Firebase not initialized');

    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const existingTags = userData.tags || [];

      // Check if tag is already assigned
      if (existingTags.includes(tagId)) {
        return; // Already assigned, skip
      }

      // Add tag to user's tags array
      await updateDoc(userRef, {
        tags: [...existingTags, tagId],
        updatedAt: new Date()
      });
    } catch (error) {
      this.log.error('Error assigning tag to user', error);
      throw error;
    }
  }

}

export const tagService = new TagService();

